import axios from "@/configuration/axios";
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoIdToken, CognitoAccessToken } from "amazon-cognito-identity-js";
import env from "@/configuration/env";
import qs from "qs";

// const
const ssoLoginRedirectUrl = `${window.location.protocol}//${window.location.host}/signin/sso/redirect`;
const ssoLogoutRedirectUrl = `${window.location.protocol}//${window.location.host}/signout`;

// vars
var pool;
var refreshInterval;

// internal functions
const shouldRefresh = state => {
  if (!state.user || !state.session) return;
  var now = new Date().getTime() / 1000;
  var exp = state.session.getIdToken().payload.exp;
  var ttl = exp - now;
  return ttl < 1500;
};

const getUserFriendlyError = value => {
  // Error Codes: https://docs.aws.amazon.com/cognito/latest/developerguide/token-endpoint.html

  if (value === "invalid_request") {
    return value;
  }

  if (value === "invalid_client") {
    return value;
  }

  if (value === "invalid_grant") {
    return value;
  }

  if (value === "unauthorized_client") {
    return value;
  }

  if (value === "unsupported_grant_type") {
    return value;
  }

  // else, just return the original value
  return value;
};

const cognitoUserPool = {
  domain: {
    get: () => localStorage.getItem("verensics:cognito:user_pool_configuration:domain"),
    set: v => localStorage.setItem("verensics:cognito:user_pool_configuration:domain", v),
    remove: () => localStorage.removeItem("verensics:cognito:user_pool_configuration:domain"),
  },
  clientId: {
    get: () => localStorage.getItem("verensics:cognito:user_pool_configuration:client_id"),
    set: v => localStorage.setItem("verensics:cognito:user_pool_configuration:client_id", v),
    remove: () => localStorage.removeItem("verensics:cognito:user_pool_configuration:client_id"),
  },
  isSSOClient: () => {
    const isSSOClient = cognitoUserPool.domain.get() && cognitoUserPool.clientId.get();
    return isSSOClient;
  },
  initialize: () => {
    const domain = cognitoUserPool.domain.get();
    const clientId = cognitoUserPool.clientId.get();
    if (domain && clientId) {
      cognitoUserPool.reconfigureForSSO(domain, clientId);
    } else {
      cognitoUserPool.reconfigureForUsernamePassword();
    }
  },
  reconfigureForUsernamePassword: () => {
    // remove sso configuration
    cognitoUserPool.domain.remove();
    cognitoUserPool.clientId.remove();

    // pool
    pool = new CognitoUserPool({
      UserPoolId: env.cognito_user_pool_id,
      ClientId: env.cognito_user_pool_client_id,
    });
  },
  reconfigureForSSO: (domain, clientId) => {
    // remove sso configuration
    cognitoUserPool.domain.set(domain);
    cognitoUserPool.clientId.set(clientId);

    // pool
    pool = new CognitoUserPool({
      UserPoolId: env.cognito_user_pool_id,
      ClientId: clientId,
    });
  },
};

const cognitoSSOState = {
  /**
   * This manages the SSO "state" query parameter.
   * This allows us to persist the SSO configuration while we redirect to cognito, the identity provider, and back to the application.
   */
  get: key => {
    try {
      // get the json serialized value from local storage
      let json = localStorage.getItem(`verensics:sso:state:${key}`);
      // deserialize json back to the map of key/value
      return JSON.parse(json);
    } finally {
      localStorage.removeItem(`verensics:sso:state:${key}`);
    }
  },
  set: (key, value) => {
    // serialize the map of key/value
    var json = JSON.stringify(value);
    // store the value in local storage
    localStorage.setItem(`verensics:sso:state:${key}`, json);
  },
};

export default {
  strict: true,
  namespaced: true,
  state: {
    isInitialized: false,
    user: null,
    session: null,
  },
  getters: {
    isInitialized: state => state.isInitialized,
    isAuthenticated: state => !!state.user,
    userId: state => (state.session ? state.session.getIdToken().payload.sub : null),
    accessToken: state => (state.session ? state.session.getAccessToken().getJwtToken() : null),
  },
  mutations: {
    initialize: state => (state.isInitialized = true),
    signIn: (state, payload) => {
      state.user = payload.user;
      state.session = payload.session;
    },
    signOut: state => {
      state.user = null;
      state.session = null;
    },
  },
  actions: {
    initialize: context =>
      new Promise(resolve => {
        // pool
        cognitoUserPool.initialize();

        // user
        const user = pool.getCurrentUser();
        if (!user) {
          context.commit("initialize");
          resolve({ success: true });
          return;
        }

        // session
        user.getSession((err, session) => {
          if (session) {
            context.commit("signIn", { user, session });
          }

          context.commit("initialize");
          resolve({ success: true });
        });

        // refresh
        refreshInterval =
          refreshInterval ||
          setInterval(() => {
            if (shouldRefresh(context.state)) {
              var refreshToken = context.state.session.getRefreshToken();
              user.refreshSession(refreshToken, (err, newSession) => {
                context.commit("signIn", { user, session: newSession });
              });
            }
          }, 60 * 1000);
      }),
    beginSSO: async (_, payload) => {
      // get SSO configuration
      const res = await axios.post("/v1/signin/sso/begin", { emailAddress: payload.username });

      // if not sso enabled
      if (!res.data.isSSOEnabled) {
        return false;
      }

      // if sso enabled, begin sso flow
      var state = {
        id: new Date().getTime(),
        cognitoUserPoolDomain: res.data.cognitoUserPoolDomain,
        cognitoUserPoolClientId: res.data.cognitoUserPoolClientId,
        cognitoUserPoolIdentityProviderName: res.data.cognitoUserPoolIdentityProviderName,
      };
      cognitoSSOState.set(state.id, state);

      var queryString = qs.stringify({
        response_type: "code",
        client_id: res.data.cognitoUserPoolClientId,
        redirect_uri: ssoLoginRedirectUrl,
        identity_provider: res.data.cognitoUserPoolIdentityProviderName,
        scope: "openid email profile",
        state: state.id,
      });

      // redirect to cognito
      window.location = `https://${res.data.cognitoUserPoolDomain}/oauth2/authorize?${queryString}`;
      return true;
    },
    completeSSO: async context => {
      const queryString = qs.parse(window.location.search, { ignoreQueryPrefix: true });

      // handle error
      if (queryString.error) {
        return {
          success: false,
          error: queryString.error,
        };
      }

      // handle code
      if (!queryString.code || !queryString.state) {
        return {
          success: false,
          error: "Unexpected response from service provider.",
        };
      }

      // state
      const state = cognitoSSOState.get(queryString.state);
      if (!state) {
        return {
          success: false,
          error: "Unexpected response from service provider.",
        };
      }

      // exchange code for JWTs
      let res;
      await axios({
        method: "POST",
        url: `https://${state.cognitoUserPoolDomain}/oauth2/token`,
        headers: { "content-type": "application/x-www-form-urlencoded" },
        data: qs.stringify({
          grant_type: "authorization_code",
          client_id: state.cognitoUserPoolClientId,
          code: queryString.code,
          redirect_uri: ssoLoginRedirectUrl,
        }),
      })
        .then(r => {
          res = r;
        })
        .catch(e => {
          res = e.response;
        });

      // handle error
      if (res.data.error) {
        return {
          success: false,
          error: getUserFriendlyError(res.data.error),
        };
      }

      // tokens
      const tokens = {
        IdToken: res.data.id_token,
        AccessToken: res.data.access_token,
        RefreshToken: res.data.refresh_token,
      };

      // jwt tokens
      const idToken = new CognitoIdToken(tokens).decodePayload();
      const accessToken = new CognitoAccessToken(tokens).jwtToken;
      const username = idToken["cognito:username"];

      // complete sso
      await axios.post("/v1/signin/sso/complete", null, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // pool
      cognitoUserPool.reconfigureForSSO(state.cognitoUserPoolDomain, state.cognitoUserPoolClientId);

      // user
      var user = new CognitoUser({
        Username: username,
        Pool: pool,
      });

      // session
      // https://github.com/amazon-archives/amazon-cognito-identity-js/issues/508
      user.signInUserSession = user.getCognitoUserSession(tokens);
      user.cacheTokens();

      // refresh
      refreshInterval =
        refreshInterval ||
        setInterval(() => {
          if (shouldRefresh(context.state)) {
            var refreshToken = context.state.session.getRefreshToken();
            user.refreshSession(refreshToken, (err, newSession) => {
              context.commit("signIn", { user, session: newSession });
            });
          }
        }, 60 * 1000);

      // vue state (user and session)
      user.getSession((err, session) => {
        context.commit("signIn", { user, session });
      });

      // response
      return { success: true };
    },
    signIn: (context, payload) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: payload.username,
        Password: payload.password,
      });

      // pool
      cognitoUserPool.reconfigureForUsernamePassword();

      // user
      const user = new CognitoUser({
        Pool: pool,
        Username: payload.username,
      });

      // session
      return new Promise((resolve, reject) =>
        user.authenticateUser(authenticationDetails, {
          onFailure: err => reject(err),
          onSuccess: session => {
            context.commit("signIn", { user, session });
            resolve({ success: true });
          },
          newPasswordRequired: () => {
            resolve({ success: false, newPasswordRequired: true });
          },
        })
      );
    },
    completeNewPasswordChallenge: (context, payload) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: payload.username,
        Password: payload.password,
      });

      // pool
      cognitoUserPool.reconfigureForUsernamePassword();

      // user
      const user = new CognitoUser({
        Pool: pool,
        Username: payload.username,
      });

      // session
      return new Promise((resolve, reject) =>
        user.authenticateUser(authenticationDetails, {
          onFailure: err => reject(err),
          onSuccess: session => {
            context.commit("signIn", { user, session });
            resolve({ success: true });
          },
          newPasswordRequired: () => {
            user.completeNewPasswordChallenge(payload.newPassword, null, {
              onFailure: err => reject(err),
              onSuccess: session => {
                context.commit("signIn", { user, session });
                resolve({ success: true });
              },
            });
          },
        })
      );
    },
    signOut: context =>
      new Promise(resolve => {
        // already logged out?
        const user = context.state.user;
        if (user === null || (user && user.tokens === null)) {
          resolve({ success: true, redirect_url: "/signin" });
          return;
        }

        // cognito javascript sdk signout
        const cognitoUser = new CognitoUser({
          Pool: pool,
          Username: user.username,
        });
        cognitoUser.signOut();

        // vue store
        context.commit("signOut");

        // redirect url
        let redirect_url = "/signin";
        if (cognitoUserPool.isSSOClient()) {
          const domain = cognitoUserPool.domain.get();
          redirect_url = `https://${domain}/logout?` + qs.stringify({ client_id: pool.clientId, logout_uri: ssoLogoutRedirectUrl });
        }

        // cognito IDP and SP signout
        resolve({ success: true, redirect_url: redirect_url });
      }),
  },
};
